import {ColumnCellClassRuleFn, ColumnGroupSpec, ColumnOrGroupSpec, ColumnSpec, ColumnState, GridSorterLike, RowClassRuleFn} from '@xh/hoist/cmp/grid';
import {PlainObject} from '@xh/hoist/core';
import {FieldFilterSpec, FilterTestFn, LockFn, parseFilter, StoreRecord} from '@xh/hoist/data';
import {isEmpty, isNil, maxBy, meanBy, minBy, omitBy, sumBy, uniq} from 'lodash';
import {formatIsComplex, formatRenderer} from '../spec/Formats';
import {leafColumns} from './GridSpecOps';
import {Aggregator, CELL_STYLE_VALUES, CellStyle, GridSpec, GridSpecColumn, isColumnGroup, StyleCondition, StylePart, StyleRule} from './GridSpecTypes';

/**
 * Pure translation of a GridSpec into Hoist grid configuration.
 */

export interface BuildColumnsOptions {
    /**
     * Host-provided base ColumnSpec for a field (renderer, cell classes etc. defined in code). The
     * spec is overlaid on top, so a spec column with no `format` keeps the host's rendering.
     */
    baseColumn?: (field: string) => ColumnSpec;
    /** Field of the tree column, for tree grids. */
    treeColumn?: string;
    /** Host hook to wrap the built columns, e.g. under a header group of its own that the spec doesn't hold. */
    wrapColumns?: (columns: ColumnOrGroupSpec[]) => ColumnOrGroupSpec[];
}

export const GROUP_HEADER_CLASS = 'gs-group-header';

export function buildColumnSpecs(spec: GridSpec, opts: BuildColumnsOptions = {}): ColumnOrGroupSpec[] {
    const ret = spec.columns.map(c =>
        isColumnGroup(c)
            ? ({
                  groupId: c.groupId,
                  headerName: c.headerName,
                  headerAlign: c.headerAlign ?? 'center',
                  headerClass: GROUP_HEADER_CLASS,
                  children: c.children.map(child => buildColumnSpec(child, opts))
              } as ColumnGroupSpec)
            : buildColumnSpec(c, opts)
    );
    return opts.wrapColumns ? opts.wrapColumns(ret) : ret;
}

function buildColumnSpec(c: GridSpecColumn, {baseColumn, treeColumn}: BuildColumnsOptions): ColumnSpec {
    const base = baseColumn?.(c.field) ?? {},
        ret: ColumnSpec = {...base, field: base.field ?? c.field, colId: c.field};

    if (c.headerName) ret.headerName = c.headerName;
    if (c.align) ret.align = c.align;
    if (c.headerAlign) ret.headerAlign = c.headerAlign;
    if (c.flex) {
        ret.flex = true;
        delete ret.width;
    } else if (c.width) {
        ret.width = c.width;
    }
    if (c.tooltip) ret.tooltip = true;
    if (treeColumn === c.field) ret.isTreeColumn = true;

    const renderer = formatRenderer(c.format);
    if (renderer) {
        ret.renderer = renderer;
        ret.rendererIsComplex = formatIsComplex(c.format);
        // Numbers read best right-aligned - unless the spec says otherwise
        if (!c.align && c.format.type === 'number') ret.align = 'right';
    }

    if (!isEmpty(c.cellClassRules)) {
        ret.cellClassRules = {...base.cellClassRules, ...buildCellClassRules(c.cellClassRules)};
    }

    return ret;
}

/** Column state (order, width, visibility, pinning) implied by the spec. */
export function buildColumnState(spec: GridSpec): ColumnState[] {
    return leafColumns(spec).map(c => {
        const ret: ColumnState = {colId: c.field, hidden: !!c.hidden, width: c.flex ? undefined : c.width};
        if (c.width && !c.flex) ret.manuallySized = true;
        if (c.pinned) ret.pinned = c.pinned;
        return ret;
    });
}

export function buildSortBy(spec: GridSpec): GridSorterLike[] {
    return spec.sortBy.map(({colId, sort, abs}) => ({colId, sort, abs: !!abs}));
}

/** Filter for a Store or Cube View query - incomplete filter rows are skipped. */
export function buildFilter(spec: GridSpec): FieldFilterSpec[] {
    return spec.filters.filter(f => f.field && f.op && f.value !== '' && !isNil(f.value)).map(({field, op, value}) => ({field, op, value}));
}

/**
 * Cube query lock for a group holding a single leaf. Its leaf would only repeat the group row, so the group gets no
 * expander. Groups with several leaves still expand. The root never locks, so an ungrouped grid keeps its rows.
 */
export const lockSingleLeafGroups: LockFn = row => row.isAggregate && row.children?.length === 1 && row.children[0].isLeaf;

//------------------------
// Style rules
//------------------------
const STYLE_PARTS = Object.keys(CELL_STYLE_VALUES) as StylePart[],
    CLASS_PREFIXES: Record<StylePart, string> = {fontWeight: 'fw', fontStyle: 'fs', color: 'color', background: 'bg'};

/** CSS class for one part of a style, e.g. `gs-color--negative` - defined in GridSpec.scss. */
export const styleClassName = (part: StylePart, value: string) => `gs-${CLASS_PREFIXES[part]}--${value}`;

/** CSS classes for a whole style - also used by the configurator to preview a rule. */
export function styleClassNames(style: CellStyle): string[] {
    return STYLE_PARTS.filter(part => style?.[part]).map(part => styleClassName(part, style[part]));
}

function buildCellClassRules(rules: StyleRule[]): Record<string, ColumnCellClassRuleFn> {
    const used = STYLE_PARTS.flatMap(part => uniq(rules.map(r => r.style?.[part]).filter(Boolean)).map(value => [part, value] as [StylePart, string]));
    return buildClassRules(() => rules, used);
}

/**
 * Row class rules - one stable rule per style class that reads the current rules via `getRules`,
 * so row styles can change without rebuilding the GridModel (a row redraw is enough).
 */
export function buildRowClassRules(getRules: () => StyleRule[]): Record<string, RowClassRuleFn> {
    const all = STYLE_PARTS.flatMap(part => CELL_STYLE_VALUES[part].map(value => [part, value] as [StylePart, string]));
    return buildClassRules(getRules, all);
}

/**
 * One ag-Grid class rule per style part and value. Each part of the style comes from the last
 * matching rule that sets it, so at most one class per part applies.
 */
function buildClassRules(getRules: () => StyleRule[], classes: [StylePart, string][]): Record<string, (params: {data: StoreRecord}) => boolean> {
    const testers = new WeakMap<StyleCondition, (r: StoreRecord) => boolean>(),
        tester = (c: StyleCondition) => {
            let ret = testers.get(c);
            if (!ret) testers.set(c, (ret = conditionTester(c)));
            return ret;
        };

    // ag-Grid evaluates every class rule for a row in turn - resolve the style once per record
    let lastRecord: StoreRecord, lastRules: StyleRule[], lastStyle: CellStyle;
    const resolve = (record: StoreRecord): CellStyle => {
        const rules = getRules();
        if (record !== lastRecord || rules !== lastRules) {
            const style: CellStyle = {};
            rules.forEach(r => {
                if (!isEmpty(r.style) && tester(r.when)(record)) Object.assign(style, omitBy(r.style, isNil));
            });
            [lastRecord, lastRules, lastStyle] = [record, rules, style];
        }
        return lastStyle;
    };

    const ret = {};
    classes.forEach(([part, value]) => {
        ret[styleClassName(part, value)] = ({data}) => resolve(data)[part] === value;
    });
    return ret;
}

function conditionTester(c: StyleCondition): (r: StoreRecord) => boolean {
    const filter = parseFilter({field: c.field, op: c.op, value: c.value});
    let testFn: FilterTestFn;
    return (record: StoreRecord) => {
        if (!record || record.isSummary) return false;
        testFn ??= filter.getTestFn(record.store);
        return testFn(record);
    };
}

//------------------------
// Totals
//------------------------
export const SUMMARY_ID = 'gs-summary';
export const SUMMARY_ROW_CLASS = 'gs-totals-row';

/**
 * Summary (totals) row data for the given records, from each column's aggregator. The label
 * 'Total' goes into `labelField` unless that column has its own aggregate.
 */
export function buildSummaryData(spec: GridSpec, records: StoreRecord[], labelField: string): PlainObject {
    const ret: PlainObject = {id: SUMMARY_ID};

    leafColumns(spec).forEach(({field, aggregator}) => {
        if (aggregator) ret[field] = aggregate(aggregator, records, field);
    });
    if (labelField && !(labelField in ret)) ret[labelField] = 'Total';

    return ret;
}

function aggregate(agg: Aggregator, records: StoreRecord[], field: string): any {
    const vals = records.filter(r => !isNil(r.data[field]));
    if (!vals.length) return agg === 'count' ? 0 : null;

    switch (agg) {
        case 'sum':
            return sumBy(vals, r => r.data[field]);
        case 'avg':
            return meanBy(vals, r => r.data[field]);
        case 'min':
            return minBy(vals, r => r.data[field]).data[field];
        case 'max':
            return maxBy(vals, r => r.data[field]).data[field];
        case 'count':
            return vals.length;
        case 'first':
            return vals[0].data[field];
        case 'last':
            return vals[vals.length - 1].data[field];
    }
}
